import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '__generated__/globalTypes';
import { FontAwesomeIcon } from 'atoms/icons';
import { Gift } from 'atoms/icons/Gift';
import { Lineups } from 'atoms/icons/Lineups';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { Carousel } from 'atoms/navigation/Carousel';
import { BodyL, BrandL, BrandM, BrandXXL } from 'atoms/typography';
import { ResponsiveImg } from 'atoms/ui/ResponsiveImg';
import { ScarcityBackground } from 'components/card/ScarcityBackground';
import { laptopAndAbove } from 'style/mediaQuery';

import commonCard from './assets/cards/common.webp';
import limitedCard from './assets/cards/limited.webp';
import rareCard from './assets/cards/rare.webp';
import superRareCard from './assets/cards/superRare.webp';
import uniqueCard from './assets/cards/unique.webp';
import { cardMessages, cardValues } from './config';

const Root = styled(Vertical).attrs({ center: true })`
  position: relative;
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    padding: calc(var(--unit) * 8);
  }
`;

const Content = styled(Vertical).attrs({ center: true })`
  position: relative;
`;

const CardDescription = styled(Vertical).attrs({ gap: 4, center: true })`
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;

const CardInformation = ({ scarcity }: { scarcity: Rarity }) => {
  const values = cardValues[scarcity as keyof typeof cardValues];
  return (
    <Vertical gap={4} center>
      <BrandL>{values.name}</BrandL>
      <Vertical gap={4} center>
        <CardDescription center gap={4}>
          <Horizontal center>
            <Lineups size={3} color="var(--c-blue-400)" />
            <BodyL>
              <FormattedMessage
                {...cardMessages.averageCost}
                values={{
                  b: <b>{values.averageCost}</b>,
                }}
              />
            </BodyL>
          </Horizontal>
          <Horizontal center>
            <Gift color="var(--c-blue-400)" />
            <BodyL>
              <FormattedMessage
                {...cardMessages.averagePoolPrize}
                values={{
                  b: <b>{values.averagePoolPrize}</b>,
                }}
              />
            </BodyL>
          </Horizontal>
          <Horizontal center>
            <FontAwesomeIcon icon={faUser} color="var(--c-blue-400)" />
            <BodyL>
              <FormattedMessage
                {...cardMessages.numberOfCompetitions}
                values={{
                  b: <b>{values.numberOfCompetitors}</b>,
                }}
              />
            </BodyL>
          </Horizontal>
        </CardDescription>
      </Vertical>
      <BodyL className="text-center">
        <FormattedMessage
          id="HowToPlay.YourBudget.description"
          defaultMessage="The rarer your cards, the bigger the rewards,
giving you the flexibility to play at your own budget and ambition level."
        />
      </BodyL>
    </Vertical>
  );
};

const cardStyle = {
  width: '200px',
  aspectRatio: 'var(--card-aspect-ratio)',
};

const elements = [
  <ResponsiveImg
    src={uniqueCard}
    alt="unique"
    key={Rarity.unique}
    style={cardStyle}
  />,
  <ResponsiveImg
    src={commonCard}
    alt="common"
    key={Rarity.common}
    style={cardStyle}
  />,
  <ResponsiveImg
    src={limitedCard}
    alt="limited"
    key={Rarity.limited}
    style={cardStyle}
  />,
  <ResponsiveImg
    src={rareCard}
    alt="rare"
    key={Rarity.rare}
    style={cardStyle}
  />,
  <ResponsiveImg
    src={superRareCard}
    alt="super rare"
    key={Rarity.super_rare}
    style={cardStyle}
  />,
];

export const YourBudget = () => {
  const [selectedIndex, setSelectedIndex] = useState(2);
  const selectedScarcity = elements[selectedIndex].key ?? Rarity.limited;

  return (
    <Root id="HowToPlay.navbar.your-budget">
      <ScarcityBackground
        rarity={selectedScarcity as Rarity}
        editionName={selectedScarcity}
      />
      <Content>
        <Vertical center gap={4}>
          <Vertical gap={4} center>
            <Vertical gap={2} center>
              <BrandM uppercase color="var(--c-limited)">
                <FormattedMessage
                  id="HowToPlay.YourBudget.eyebrow"
                  defaultMessage="Card Scarcities"
                />
              </BrandM>
              <BrandXXL uppercase className="text-center">
                <FormattedMessage
                  id="HowToPlay.YourBudget.title"
                  defaultMessage="Your Budget, your stakes"
                />
              </BrandXXL>
            </Vertical>
            <BodyL>
              <FormattedMessage
                id="HowToPlay.YourBudget.subTitle"
                defaultMessage="Choose the scarcity level that fits your strategy and collection."
              />
            </BodyL>
          </Vertical>
          <Carousel
            elements={elements}
            renderElement={element => element}
            selectedIndex={selectedIndex}
            onClick={(_, index) => {
              setSelectedIndex(index);
            }}
          />
          <CardInformation scarcity={selectedScarcity as Rarity} />
        </Vertical>
      </Content>
    </Root>
  );
};
